import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Tables } from '@/integrations/supabase/types';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';

export interface GoFundMeDonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  charity: Tables<'charities'>;
  sequenceTitle: string;
  sequenceArtist: string;
  /** Callback triggered after user dismisses modal */
  onAfterDismiss?: () => void;
}

export type GoFundMeWidgetSize = 'large' | 'medium';

export interface GoFundMeEmbedProps {
  url: string;
  size: GoFundMeWidgetSize;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Responsive GoFundMe embed component that handles different widget sizes
 */
export const GoFundMeEmbed: React.FC<GoFundMeEmbedProps> = ({
  url,
  size,
  onLoad,
  onError
}) => {
  const embedRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Extract campaign identifier from GoFundMe URL
  const getCampaignPath = (gofundmeUrl: string): string => {
    try {
      const urlObj = new URL(gofundmeUrl);
      // Extract path like "/f/campaign-name" from "https://www.gofundme.com/f/campaign-name"
      return urlObj.pathname;
    } catch (error) {
      console.warn('Invalid GoFundMe URL:', gofundmeUrl);
      return '/f/invalid-campaign';
    }
  };

  // Generate widget URL based on size and campaign
  const getWidgetUrl = (gofundmeUrl: string, widgetSize: GoFundMeWidgetSize): string => {
    const campaignPath = getCampaignPath(gofundmeUrl);
    const baseUrl = 'https://www.gofundme.com';
    return `${baseUrl}${campaignPath}/widget/${widgetSize}?sharesheet=CAMPAIGN_PAGE&attribution_id=light-hunt`;
  };

  useEffect(() => {
    if (!embedRef.current) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      const widgetUrl = getWidgetUrl(url, size);
      
      // Clear previous embed
      embedRef.current.innerHTML = '';
      
      // Create embed container
      const embedDiv = document.createElement('div');
      embedDiv.className = 'gfm-embed';
      embedDiv.setAttribute('data-url', widgetUrl);
      
      embedRef.current.appendChild(embedDiv);

      // Load GoFundMe embed script
      const existingScript = document.querySelector('script[src*="gofundme.com/static/js/embed.js"]');
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://www.gofundme.com/static/js/embed.js';
        script.defer = true;
        
        script.onload = () => {
          setIsLoading(false);
          onLoad?.();
        };
        
        script.onerror = () => {
          const error = new Error('Failed to load GoFundMe embed script');
          setLoadError(error.message);
          setIsLoading(false);
          onError?.(error);
        };
        
        document.head.appendChild(script);
      } else {
        // Script already exists, trigger load callback after short delay
        setTimeout(() => {
          setIsLoading(false);
          onLoad?.();
        }, 100);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error occurred');
      setLoadError(err.message);
      setIsLoading(false);
      onError?.(err);
    }
  }, [url, size, onLoad, onError]);

  if (loadError) {
    return (
      <div className="p-6 text-center bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 mb-2">Failed to load donation widget</p>
        <p className="text-sm text-red-500">{loadError}</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-red-700 underline hover:text-red-800 transition-colors"
        >
          Visit donation page directly
        </a>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading donation widget...</span>
        </div>
      )}
      <div 
        ref={embedRef} 
        className={`gofundme-embed-container ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        data-testid="gofundme-embed"
      />
    </div>
  );
};

/**
 * GoFundMe Donation Modal - Shows donation widget for free downloads when creator has a charity
 */
export const GoFundMeDonationModal: React.FC<GoFundMeDonationModalProps> = ({
  isOpen,
  onClose,
  charity,
  sequenceTitle,
  sequenceArtist,
  onAfterDismiss
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const { setDonationModalShown } = useDownloadFlowStore();

  // Determine widget size based on screen size
  const [widgetSize, setWidgetSize] = useState<GoFundMeWidgetSize>('large');

  useEffect(() => {
    const updateWidgetSize = () => {
      // Use medium for mobile/tablet, large for desktop
      setWidgetSize(window.innerWidth < 1024 ? 'medium' : 'large');
    };

    updateWidgetSize();
    window.addEventListener('resize', updateWidgetSize);
    return () => window.removeEventListener('resize', updateWidgetSize);
  }, []);

  // Handle modal close with proper flow management
  const handleClose = async () => {
    setIsClosing(true);
    setDonationModalShown(true);
    
    // Brief delay for animation
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      onAfterDismiss?.();
    }, 150);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle embed loading states
  const handleEmbedLoad = () => {
    setEmbedLoaded(true);
    setEmbedError(null);
  };

  const handleEmbedError = (error: Error) => {
    setEmbedError(error.message);
    setEmbedLoaded(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      data-testid="donation-modal-overlay"
      onClick={(e) => {
        // Only close if clicking the overlay, not the modal content
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className={`relative w-full max-w-4xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden transition-transform duration-200 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        data-testid="donation-modal"
        role="dialog"
        aria-labelledby="donation-modal-title"
        aria-describedby="donation-modal-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 
              id="donation-modal-title" 
              className="text-xl font-semibold text-gray-900"
            >
              Support {charity.name}
            </h2>
            <p 
              id="donation-modal-description" 
              className="text-sm text-gray-600 mt-1"
            >
              Consider supporting this cause while downloading "{sequenceTitle}" by {sequenceArtist}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close donation modal"
            data-testid="close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Charity Description */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">About This Cause</h3>
            <p className="text-gray-700">{charity.description}</p>
            {charity.supporting_text && (
              <p className="text-gray-600 mt-2 text-sm">{charity.supporting_text}</p>
            )}
          </div>

          {/* GoFundMe Embed */}
          <div className="mb-6">
            <GoFundMeEmbed
              url={charity.url}
              size={widgetSize}
              onLoad={handleEmbedLoad}
              onError={handleEmbedError}
            />
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
              data-testid="continue-button"
            >
              Continue to Download
            </button>
            <a
              href={charity.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-center font-medium"
              data-testid="visit-campaign-button"
            >
              Visit Campaign Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoFundMeDonationModal; 